import api from "../httpClient"

export type MeetingResource = {
  id: string
  media_asset: string
  storage_key: string
  media_type: string
  original_filename?: string | null
}

export type MeetingHost = {
  id?: number | string | null
  name?: string | null
  email?: string | null
  avatar_url?: string | null
  is_guest?: boolean
}

export type MeetingQuestionSubmissionStatus = "submitted" | "reviewed" | "answered" | "archived"

export type MeetingQuestionSubmission = {
  id: string
  meeting: string
  author: MeetingHost
  body: string
  status: MeetingQuestionSubmissionStatus
  host_note: string
  reviewed_by?: MeetingHost | null
  reviewed_at?: string | null
  created_at: string
  updated_at: string
}

export type MeetingQuestionsPanelRole = "reviewer" | "participant" | "guest"

export type MeetingQuestionsPanel = {
  enabled: boolean
  role: MeetingQuestionsPanelRole
  url: string
}

export type MeetingMessagesPanelRole = "reviewer" | "participant" | "guest"

export type MeetingMessagesPanel = {
  enabled: boolean
  role: MeetingMessagesPanelRole
  url: string
}

export type MeetingInspirePanelRole = "presenter" | "listener"

export type MeetingInspirePanel = {
  enabled: boolean
  role: MeetingInspirePanelRole
  url: string
  controls_url?: string
}

export type MeetingPollPanel = {
  enabled: boolean
  role: "presenter" | "listener"
  panel_token: string
  pending_admission?: boolean
}
export type MeetingPollVote = { option_ids: string[] }
export type MeetingPoll = {
  id: string
  question: string
  poll_type: "single" | "multiple" | "rating"
  visibility_mode: "named" | "anonymous"
  options: { id: string; label: string }[]
  rating_min_label?: string
  rating_max_label?: string
  status: "open" | "closed"
  results_visible: boolean
  displayed_at: string | null
  launched_at: string
  closed_at: string | null
  own_vote: MeetingPollVote | null
  results?: {
    respondent_count: number
    options: { id: string; count: number; percentage: number }[]
    average: number | null
  }
}
export type MeetingPollCreate = {
  request_id: string
  question: string
  poll_type: MeetingPoll["poll_type"]
  visibility_mode: MeetingPoll["visibility_mode"]
  results_visible?: boolean
  options: string[]
  rating_min_label?: string
  rating_max_label?: string
  /** The presenter confirmed putting a live Inspire Card away; without it a live card answers 409 `inspire_active`. */
  end_inspire_card?: boolean
}
export type MeetingPollPanelResponse = {
  role: MeetingPollPanel["role"]
  active_poll: MeetingPoll | null
  displayed_poll: MeetingPoll | null
  selected_poll?: MeetingPoll | null
  polls?: MeetingPoll[]
  own_vote: MeetingPollVote | null
  realtime_url: string | null
  inspire_active: boolean
}
export type MeetingPollResponsesPage = {
  count: number
  page: number
  next_page: number | null
  results: { id: string; display_name: string; option_ids: string[] }[]
}
export type MeetingPollHistory = {
  polls: MeetingPoll[]
  selected_poll_id: string | null
  responses: MeetingPollResponsesPage | null
}

export type MeetingInspireCard = {
  id: string
  key: string
  prompt: string
  helper_text: string
  facilitator_notes: string
  uses_deck_helper_text: boolean
  uses_deck_facilitator_notes: boolean
  version: number
  media_url?: string | null
}

export type MeetingInspireDeck = {
  id: string
  key: string
  name: string
  description: string
  helper_text: string
  facilitator_notes: string
  accent_color: string
  cards: MeetingInspireCard[]
}

export type MeetingInspireActivity = {
  id: string
  visibility_mode: "named" | "anonymous"
  status: "active" | "ended"
  card_key_snapshot: string
  card_prompt_snapshot: string
  card_helper_text_snapshot: string
  facilitator_notes_snapshot?: string
  card_version_snapshot: number
  deck_name_snapshot: string
  deck_accent_color_snapshot: string
  program_name_snapshot: string
  program_logo_url_snapshot?: string
  launched_at: string
  ended_at?: string | null
  response_count: number
  shared_response_id?: string | null
}

export type MeetingInspireResponse = {
  id: string
  display_name: string
  body: string
  created_at: string
  updated_at: string
}

export type MeetingInspirePanelResponse = {
  role: MeetingInspirePanelRole
  enabled: boolean
  branding: { name: string; logo_url?: string | null }
  active_activity?: MeetingInspireActivity | null
  activities?: MeetingInspireActivity[]
  latest_activity?: MeetingInspireActivity | null
  shared_activity?: MeetingInspireActivity | null
  shared_response?: MeetingInspireResponse | null
  decks?: MeetingInspireDeck[]
  response?: MeetingInspireResponse | null
  responses?: MeetingInspireResponse[]
  responses_count?: number
  realtime_url?: string | null
}

export type MeetingInspireResponsesPage = {
  count: number
  page: number
  page_size: number
  next_page?: number | null
  results: MeetingInspireResponse[]
}

export type MeetingInspireResponseShareResult = {
  activity: MeetingInspireActivity
  shared_response: MeetingInspireResponse | null
}

export type MeetingInspireHistoryResponse = {
  activities: MeetingInspireActivity[]
  selected_activity_id?: string | null
  responses: MeetingInspireResponsesPage
}

export type MeetingQuestionsPanelResponse = {
  role: MeetingQuestionsPanelRole
  question_collection_enabled: boolean
  meeting_status?: string
  realtime_url?: string | null
  submissions: MeetingQuestionSubmission[]
}

export type MeetingDirectMessageParticipant = {
  identity_key: string
  display_name: string
  is_guest: boolean
  avatar_url?: string | null
  last_seen_at?: string | null
}

export type MeetingDirectMessage = {
  id: string
  thread: string
  sender_identity_key: string
  body: string
  is_own: boolean
  created_at: string
}

export type MeetingDirectMessageThread = {
  id: string
  recipient: MeetingDirectMessageParticipant
  last_message?: MeetingDirectMessage | null
  unread_count: number
  updated_at: string
}

export type MeetingMessagesPanelResponse = {
  role: MeetingMessagesPanelRole
  identity_key: string
  display_name: string
  realtime_url?: string | null
  recipients: MeetingDirectMessageParticipant[]
  threads: MeetingDirectMessageThread[]
}

export type MeetingDirectMessageThreadMessagesResponse = {
  thread: MeetingDirectMessageThread
  messages: MeetingDirectMessage[]
}

export type MeetingPanelEventPayload = {
  type: string
  meeting_id?: string
  thread_id?: string
  submission_id?: string
  recipient_keys?: string[]
  role_targets?: string[]
  owner_identity_key?: string
  created_at?: string
  [key: string]: unknown
}

export type RecurrencePayload = {
  interval: number
  period: "day" | "week" | "month"
  days_of_week?: Array<"sunday" | "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday">
  start_date: string
  end_date?: string | null
  no_end_date?: boolean
}

export type RecurrenceResponse = RecurrencePayload & {
  series_id?: string | null
  materialized_until?: string | null
}

export type MeetingResponse = {
  id: string
  title: string
  description?: string | null
  host?: MeetingHost | null
  cohort_id?: string | null
  start_time: string
  end_time: string
  actual_ended_at?: string | null
  manually_ended_at?: string | null
  can_join_video?: boolean
  active_participant_count?: number
  late_join_until?: string | null
  time_zone: string
  video_url?: string | null
  location?: string | null
  status: "upcoming" | "past" | "cancelled"
  is_virtual: boolean
  is_milestone?: boolean
  guest_access_enabled?: boolean
  guest_join_url?: string | null
  waiting_room_enabled?: boolean
  waiting_room_unlocked?: boolean
  waiting_room_unlocked_at?: string | null
  question_collection_enabled?: boolean
  question_submission_count?: number
  unreviewed_question_submission_count?: number
  can_review_questions?: boolean
  transcription_status?: "pending" | "started" | "available" | "failed"
  transcript_text?: string | null
  summary_text?: string | null
  recording_enabled?: boolean
  recording_status?: "pending" | "copying" | "available" | "failed" | null
  recording_available?: boolean
  invitees_mode?: "cohort" | "mentors" | "scholars" | "custom" | null
  invited_user_ids: Array<string | number>
  attendance_tracked?: boolean
  resources: MeetingResource[]
  recurrence?: RecurrenceResponse | null
  series_id?: string | null
  is_exception?: boolean
  original_occurrence_date?: string | null
  occurrence_status?: "normal" | "edited" | "cancelled"
  cancelled?: boolean
}

export type MeetingAttendanceEntry = {
  attendee_id?: string
  attendee_type?: "user" | "guest"
  user_id: number | string | null
  display_name?: string
  role?: string
  is_host?: boolean
  attended: boolean
  source?: "manual" | "daily" | "meet_audit" | null
  locked: boolean
  recorded_by_id?: number | string | null
  updated_at?: string | null
}

export type ManualGuestAttendee = {
  display_name: string
}

export type MeetingAttendanceResponse = {
  meeting_id: string
  meeting_ended: boolean
  can_edit: boolean
  attendees: MeetingAttendanceEntry[]
}

export type ChecklistTemplateSummary = {
  id: string
  title: string
  description?: string
  due_offset_days?: number | null
  external_url?: string | null
  completed_count: number
  due_date?: string | null
  due_date_varies?: boolean
  media_asset?: {
    id: string
    storage_key: string
    media_type: string
    mime_type?: string | null
    alt_text?: string | null
    is_active?: boolean
    url?: string | null
  } | null
  users?: Array<{
    id: string
    name: string
    completed: boolean
    completed_at?: string | null
  }>
}

export type ChecklistRoleSummary = {
  total_users: number
  templates: ChecklistTemplateSummary[]
}

export type SafetySignalChecklistItem = {
  id: string
  created_at: string
  completed_at?: string | null
}

export type SafetySignal = {
  id: number
  user_id: number
  user_name: string
  user_role?: string | null
  mentor_id?: number | null
  mentor_name?: string | null
  summary: string
  metadata: Record<string, unknown>
  priority: "low" | "medium" | "high"
  source: string
  created_at: string
  viewed_at?: string | null
  mentor_notified_at?: string | null
  admin_note?: string | null
  resolved_at?: string | null
  dismissed_at?: string | null
  status: "new" | "in_progress" | "complete"
  mentor_checklist_item?: SafetySignalChecklistItem | null
  last_updated_by?: {
    id: number
    name: string
    email?: string | null
  } | null
}

export type AdminDashboardResponse = {
  cohort?: {
    id: string
    name: string
    description?: string | null
    is_active: boolean
    mentee_label_singular?: string | null
    mentee_label_plural?: string | null
    mentor_label_singular?: string | null
    mentor_label_plural?: string | null
    start_date?: string | null
    end_date?: string | null
    organization?: {
      id: string
      name: string
      org_id: string
    } | null
  } | null
  meetings: MeetingResponse[]
  roster: any[]
  checklist_summary?: {
    mentors: ChecklistRoleSummary
    scholars: ChecklistRoleSummary
  }
  safety_signals?: SafetySignal[]
}

type PaginatedResponse<T> = {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

const getMeetings = async (params?: { scope?: "upcoming" | "past" | "all"; admin_scope?: "all" }) => {
  const resp = await api.get<MeetingResponse[] | PaginatedResponse<MeetingResponse>>("/api/meetings/", { params })
  return resp.data
}

const getMeeting = async (meetingId: string) => {
  const resp = await api.get<MeetingResponse>(`/api/meetings/${meetingId}/`)
  return resp.data
}

const getMeetingAttendance = async (meetingId: string) => {
  const resp = await api.get<MeetingAttendanceResponse>(`/api/meetings/${meetingId}/attendance/`)
  return resp.data
}

const saveMeetingAttendance = async (
  meetingId: string,
  attendedUserIds: Array<string | number>,
  manualGuestAttendees: ManualGuestAttendee[] = [],
) => {
  const resp = await api.patch<MeetingAttendanceResponse>(`/api/meetings/${meetingId}/attendance/`, {
    attended_user_ids: attendedUserIds,
    manual_guest_attendees: manualGuestAttendees,
  })
  return resp.data
}

const downloadResource = async (meetingId: string, resourceId: string) => {
  const resp = await api.get<{ url: string }>(`/api/meetings/${meetingId}/resources/${resourceId}/download/`)
  return resp.data
}

const downloadTranscript = async (meetingId: string) => {
  const resp = await api.get<Blob>(`/api/meetings/${meetingId}/transcript/`, {
    responseType: "blob",
  })
  return resp.data
}

const downloadRecording = async (meetingId: string) => {
  const resp = await api.get<{ url: string; filename?: string }>(`/api/meetings/${meetingId}/recording/`)
  return resp.data
}

const getMeetingQuestions = async (meetingId: string) => {
  const resp = await api.get<MeetingQuestionSubmission[]>(`/api/meetings/${meetingId}/questions/`)
  return resp.data
}

const panelTokenHeaders = (panelToken: string) => ({
  headers: {
    "X-Meeting-Panel-Token": panelToken,
  },
})

const panelTokenRequestConfig = (panelToken: string, timeout = 15000) => ({
  ...panelTokenHeaders(panelToken),
  timeout,
})

const getMeetingQuestionsPanel = async (meetingId: string, panelToken: string) => {
  const resp = await api.get<MeetingQuestionsPanelResponse>(
    `/api/meetings/${meetingId}/questions-panel/`,
    panelTokenRequestConfig(panelToken),
  )
  return resp.data
}

const submitMeetingQuestionsPanelQuestion = async (
  meetingId: string,
  panelToken: string,
  payload: { body: string },
) => {
  const resp = await api.post<MeetingQuestionSubmission>(
    `/api/meetings/${meetingId}/questions-panel/questions/`,
    payload,
    panelTokenRequestConfig(panelToken),
  )
  return resp.data
}

const updateMeetingQuestionsPanelQuestion = async (
  meetingId: string,
  panelToken: string,
  submissionId: string,
  payload: Partial<Pick<MeetingQuestionSubmission, "status" | "host_note">>,
) => {
  const resp = await api.patch<MeetingQuestionSubmission>(
    `/api/meetings/${meetingId}/questions-panel/questions/${submissionId}/`,
    payload,
    panelTokenRequestConfig(panelToken),
  )
  return resp.data
}

const getMeetingMessagesPanel = async (meetingId: string, panelToken: string) => {
  const resp = await api.get<MeetingMessagesPanelResponse>(
    `/api/meetings/${meetingId}/messages-panel/`,
    panelTokenRequestConfig(panelToken),
  )
  return resp.data
}

const updateMeetingMessagesPresence = async (
  meetingId: string,
  panelToken: string,
  payload: { daily_participant_id?: string } = {},
) => {
  const resp = await api.post<MeetingDirectMessageParticipant>(
    `/api/meetings/${meetingId}/messages-panel/presence/`,
    payload,
    panelTokenRequestConfig(panelToken),
  )
  return resp.data
}

const createMeetingDirectMessageThread = async (
  meetingId: string,
  panelToken: string,
  payload: { recipient_identity_key: string },
) => {
  const resp = await api.post<MeetingDirectMessageThread>(
    `/api/meetings/${meetingId}/messages-panel/threads/`,
    payload,
    panelTokenRequestConfig(panelToken),
  )
  return resp.data
}

const getMeetingDirectMessageThreadMessages = async (
  meetingId: string,
  panelToken: string,
  threadId: string,
) => {
  const resp = await api.get<MeetingDirectMessageThreadMessagesResponse>(
    `/api/meetings/${meetingId}/messages-panel/threads/${threadId}/messages/`,
    panelTokenRequestConfig(panelToken),
  )
  return resp.data
}

const sendMeetingDirectMessage = async (
  meetingId: string,
  panelToken: string,
  threadId: string,
  payload: { body: string },
) => {
  const resp = await api.post<MeetingDirectMessage>(
    `/api/meetings/${meetingId}/messages-panel/threads/${threadId}/messages/`,
    payload,
    panelTokenRequestConfig(panelToken),
  )
  return resp.data
}

const markMeetingDirectMessageThreadRead = async (
  meetingId: string,
  panelToken: string,
  threadId: string,
) => {
  const resp = await api.post<MeetingDirectMessageThread>(
    `/api/meetings/${meetingId}/messages-panel/threads/${threadId}/read/`,
    {},
    panelTokenRequestConfig(panelToken, 10000),
  )
  return resp.data
}

const submitMeetingQuestion = async (
  meetingId: string,
  payload: { body: string },
) => {
  const resp = await api.post<MeetingQuestionSubmission>(`/api/meetings/${meetingId}/questions/`, payload)
  return resp.data
}

const updateMeetingQuestion = async (
  meetingId: string,
  submissionId: string,
  payload: Partial<Pick<MeetingQuestionSubmission, "status" | "host_note">>,
) => {
  const resp = await api.patch<MeetingQuestionSubmission>(
    `/api/meetings/${meetingId}/questions/${submissionId}/`,
    payload,
  )
  return resp.data
}

export type MeetingUpsertPayload = {
  title: string
  description?: string
  cohort_id?: string | null
  start_time: string
  end_time: string
  start_time_local?: string
  end_time_local?: string
  time_zone?: string
  is_virtual?: boolean
  is_milestone?: boolean
  guest_access_enabled?: boolean
  waiting_room_enabled?: boolean
  question_collection_enabled?: boolean
  recording_enabled?: boolean
  invitees_mode?: "cohort" | "mentors" | "scholars" | "custom" | null
  invited_user_ids?: Array<string | number>
  attachment_ids?: string[]
  recurrence?: RecurrencePayload | null
}

const createMeeting = async (payload: MeetingUpsertPayload) => {
  const resp = await api.post<MeetingResponse>("/api/meetings/", payload)
  return resp.data
}

const updateMeeting = async (meetingId: string, payload: Partial<MeetingUpsertPayload>) => {
  const resp = await api.patch<MeetingResponse>(`/api/meetings/${meetingId}/`, payload)
  return resp.data
}

const deleteMeeting = async (meetingId: string) => {
  await api.delete(`/api/meetings/${meetingId}/`)
}

const joinMeeting = async (meetingId: string) => {
  const resp = await api.post<{
    url: string
    token?: string | null
    requires_admission?: boolean
    native_calls_enabled?: boolean
    waiting_room_enabled?: boolean
    waiting_room_unlocked?: boolean
    can_unlock_wait_room?: boolean
    can_start_transcription?: boolean
    continuity?: { role: "mentor" | "mentee"; relationship_id?: string | null; mentee_id?: number | string | null; pair_meeting?: boolean }
    display_name?: string
    questions_panel?: MeetingQuestionsPanel
    messages_panel?: MeetingMessagesPanel
    inspire_panel?: MeetingInspirePanel
  poll_panel?: MeetingPollPanel
  }>(`/api/meetings/${meetingId}/join/`, { call_protocol: 2 })
  return resp.data
}

export type GuestMeetingJoinResponse = {
  meeting: {
    id: string
    title: string
    start_time: string | null
    end_time: string | null
    question_collection_enabled?: boolean
      host?: {
      name?: string | null
    } | null
  }
  url: string
  token?: string | null
  requires_admission?: boolean
  native_calls_enabled?: boolean
  waiting_room_enabled?: boolean
  waiting_room_unlocked?: boolean
  can_unlock_wait_room?: boolean
  display_name?: string
  guest_session_id?: string
  breakout_token?: string | null
  questions_panel?: MeetingQuestionsPanel
  messages_panel?: MeetingMessagesPanel
  inspire_panel?: MeetingInspirePanel
  poll_panel?: MeetingPollPanel
}

const joinGuestMeeting = async (
  meetingId: string,
  payload: { guest_token: string; display_name: string; guest_session_id?: string; breakout_token?: string | null },
) => {
  const resp = await api.post<GuestMeetingJoinResponse>(`/api/meetings/${meetingId}/guest-join/`, { ...payload, call_protocol: 2 })
  return resp.data
}

const submitGuestMeetingQuestion = async (
  meetingId: string,
  payload: { guest_token: string; display_name: string; body: string; guest_session_id?: string },
) => {
  const resp = await api.post<MeetingQuestionSubmission>(`/api/meetings/${meetingId}/guest-questions/`, payload)
  return resp.data
}

// ---------------------------------------------------------------- breakout rooms (custom call)
export type BreakoutMember = { identity_key: string; display_name: string; daily_participant_id?: string }
export type BreakoutRoom = { id: string; name: string; position: number; members: BreakoutMember[]; transcription_status?: "pending" | "started" | "available" | "failed" }
export type BreakoutSession = {
  main_room_members?: BreakoutMember[]
  id: string
  status: "active" | "ended"
  auto_join: boolean
  started_at?: string | null
  /** Set by "End in 60 seconds": when the session will end. */
  scheduled_end_at?: string | null
  ended_at?: string | null
  rooms: BreakoutRoom[]
  unassigned: BreakoutMember[]
}
export type BreakoutAssignment = { room_id: string; room_name: string; url: string; token: string }
/** Metadata only. The native SDK observation, never this response, proves presence. */
export type NativeBreakoutContext = {
  version: 1; revision: string; meeting_id: string; access_id: string; observed_room_url: string; main_room_url: string;
  subject: string; daily_user_id: string; is_owner: boolean;
  status: 'main' | 'breakout' | 'return_required' | 'terminal'; reason?: string;
  room: { id: string; name: string; session_id: string } | null;
  session: BreakoutSession | null;
  assignment: Pick<BreakoutAssignment, 'room_id' | 'room_name'> | null;
}
export type NativeContextInput = { panel_token?: string; access_id: string; observed_room_url: string; breakout_token?: string; guest_token?: string }
const fetchNativeBreakoutContext = async (meetingId: string, payload: NativeContextInput): Promise<NativeBreakoutContext> =>
  (await api.post<NativeBreakoutContext>(`/api/meetings/${meetingId}/mobile-access/context/`, payload)).data
const renewNativeCallTools = async (meetingId: string, payload: NativeContextInput): Promise<{
  access_id: string; subject: string; meeting: MeetingResponse; join_info: import("pages/Meetings/call/types").JoinInfo
}> => (await api.post<{ access_id: string; subject: string; meeting: MeetingResponse; join_info: import("pages/Meetings/call/types").JoinInfo }>(`/api/meetings/${meetingId}/mobile-access/tools/`, payload)).data
const renewNativeRoomTarget = async (meetingId: string, payload: NativeContextInput & { room_id?: string }): Promise<{ access_id: string; url: string; token: string; room_id: string | null; room_name: string | null }> =>
  (await api.post<{ access_id: string; url: string; token: string; room_id: string | null; room_name: string | null }>(`/api/meetings/${meetingId}/mobile-access/room-target/`, payload)).data

export type BreakoutCurrentResponse = { session: BreakoutSession | null; assignment: BreakoutAssignment | null }
export type BreakoutRoomSpec = { name: string; members: BreakoutMember[] }
export type BreakoutMove = BreakoutMember & { room_id: string | null }

export type MentorBreakoutGroup = { mentor: { id: number; name: string }; mentees: Array<{ id: number; name: string }> }
export type MentorBreakoutPlan = { cohort_id: string | null; groups: MentorBreakoutGroup[]; version: string }

export const fetchMentorBreakoutPlan = async (meetingId: string): Promise<MentorBreakoutPlan> => {
  const resp = await api.get<MentorBreakoutPlan>(`/api/meetings/${meetingId}/mentor-breakout-plan/`)
  return resp.data
}

const createBreakoutSession = async (meetingId: string, payload: { auto_join: boolean; rooms: BreakoutRoomSpec[]; mentorship_version?: string }) => {
  const resp = await api.post<{ session: BreakoutSession }>(`/api/meetings/${meetingId}/breakouts/`, payload)
  return resp.data
}

const fetchBreakoutCurrent = async (meetingId: string, payload: { room_id?: string } = {}) => {
  const resp = await api.post<BreakoutCurrentResponse>(`/api/meetings/${meetingId}/breakouts/current/`, payload)
  return resp.data
}

const fetchGuestBreakoutCurrent = async (
  meetingId: string,
  payload: { guest_token: string; breakout_token?: string | null; admission_token?: string; display_name?: string },
) => {
  const resp = await api.post<BreakoutCurrentResponse>(`/api/meetings/${meetingId}/breakouts/guest-current/`, payload)
  return resp.data
}

const updateBreakoutAssignments = async (meetingId: string, sessionId: string, moves: BreakoutMove[]) => {
  const resp = await api.post<{ session: BreakoutSession }>(`/api/meetings/${meetingId}/breakouts/${sessionId}/assignments/`, { moves })
  return resp.data
}

const broadcastBreakoutMessage = async (meetingId: string, sessionId: string, message: string) => {
  const resp = await api.post<{ status: string; message: string }>(`/api/meetings/${meetingId}/breakouts/${sessionId}/broadcast/`, { message })
  return resp.data
}

const endBreakoutSession = async (meetingId: string, sessionId: string, delaySeconds = 0) => {
  const resp = await api.post<{ session: BreakoutSession }>(`/api/meetings/${meetingId}/breakouts/${sessionId}/end/`, { delay_seconds: delaySeconds })
  return resp.data
}

const endMeeting = async (meetingId: string) => {
  const resp = await api.post<{ status: string; ended_at: string }>(`/api/meetings/${meetingId}/end/`)
  return resp.data
}

const panelHeaders = (panelToken: string) => ({ "X-Meeting-Panel-Token": panelToken })

const getMeetingPollPanel = async (meetingId: string, panelToken: string) => {
  const response = await api.get<MeetingPollPanelResponse>(`/api/meetings/${meetingId}/poll-panel/`, { headers: panelHeaders(panelToken) })
  return response.data
}
const admitMeetingPollParticipant = async (meetingId: string, panelToken: string, participantToken: string, dailyUserId?: string) => {
  const response = await api.post<MeetingPollPanel>(`/api/meetings/${meetingId}/poll-panel/admit/`, { participant_panel_token: participantToken, daily_user_id: dailyUserId }, { headers: panelHeaders(panelToken) })
  return response.data
}
const createMeetingPoll = async (meetingId: string, panelToken: string, payload: MeetingPollCreate) => {
  const response = await api.post<MeetingPoll>(`/api/meetings/${meetingId}/poll-panel/polls/`, payload, { headers: panelHeaders(panelToken) })
  return response.data
}
const saveMeetingPollVote = async (meetingId: string, pollId: string, panelToken: string, optionIds: string[]) => {
  const response = await api.put<MeetingPollVote>(`/api/meetings/${meetingId}/poll-panel/polls/${pollId}/vote/`, { option_ids: optionIds }, { headers: panelHeaders(panelToken) })
  return response.data
}
const controlMeetingPoll = async (meetingId: string, pollId: string, panelToken: string, action: "close" | "reveal" | "dismiss") => {
  const response = await api.post<MeetingPoll>(`/api/meetings/${meetingId}/poll-panel/polls/${pollId}/${action}/`, {}, { headers: panelHeaders(panelToken) })
  return response.data
}
const getMeetingPollResponses = async (meetingId: string, pollId: string, panelToken: string, page = 1) => {
  const response = await api.get<MeetingPollResponsesPage>(`/api/meetings/${meetingId}/poll-panel/polls/${pollId}/responses/`, { params: { page }, headers: panelHeaders(panelToken) })
  return response.data
}
const getMeetingPollHistory = async (meetingId: string, params?: { poll_id?: string; page?: number }) => {
  const response = await api.get<MeetingPollHistory>(`/api/meetings/${meetingId}/poll-history/`, { params })
  return response.data
}

const getMeetingInspirePanel = async (meetingId: string, panelToken: string) => {
  const resp = await api.get<MeetingInspirePanelResponse>(`/api/meetings/${meetingId}/inspire-panel/`, {
    headers: panelHeaders(panelToken),
  })
  return resp.data
}

const createMeetingInspireActivity = async (
  meetingId: string,
  panelToken: string,
  payload: (
    | { deck_id: string; card_id: string; visibility_mode: "named" | "anonymous" }
    | { custom_prompt: string; visibility_mode: "named" | "anonymous" }
  ) & {
    /** The presenter confirmed closing the open poll; without it an open poll answers 409 `poll_open`. */
    close_open_poll?: boolean
  },
) => {
  const resp = await api.post<MeetingInspireActivity>(
    `/api/meetings/${meetingId}/inspire-panel/activities/`,
    payload,
    { headers: panelHeaders(panelToken) },
  )
  return resp.data
}

const endMeetingInspireActivity = async (meetingId: string, activityId: string, panelToken: string) => {
  const resp = await api.post<MeetingInspireActivity>(
    `/api/meetings/${meetingId}/inspire-panel/activities/${activityId}/end/`,
    {},
    { headers: panelHeaders(panelToken) },
  )
  return resp.data
}

const saveMeetingInspireResponse = async (
  meetingId: string,
  activityId: string,
  panelToken: string,
  payload: { body: string },
) => {
  const resp = await api.put<MeetingInspireResponse>(
    `/api/meetings/${meetingId}/inspire-panel/activities/${activityId}/response/`,
    payload,
    { headers: panelHeaders(panelToken) },
  )
  return resp.data
}

const getMeetingInspireResponses = async (
  meetingId: string,
  activityId: string,
  panelToken: string,
  page = 1,
) => {
  const resp = await api.get<MeetingInspireResponsesPage>(
    `/api/meetings/${meetingId}/inspire-panel/activities/${activityId}/responses/`,
    { params: { page }, headers: panelHeaders(panelToken) },
  )
  return resp.data
}

const shareMeetingInspireResponse = async (
  meetingId: string,
  activityId: string,
  panelToken: string,
  responseId: string | null,
) => {
  const resp = await api.post<MeetingInspireResponseShareResult>(
    `/api/meetings/${meetingId}/inspire-panel/activities/${activityId}/share-response/`,
    { response_id: responseId },
    { headers: panelHeaders(panelToken) },
  )
  return resp.data
}

const getMeetingInspireHistory = async (meetingId: string, params?: { activity_id?: string; page?: number }) => {
  const resp = await api.get<MeetingInspireHistoryResponse>(`/api/meetings/${meetingId}/inspire-activities/`, { params })
  return resp.data
}

const createRecurrence = async (payload: MeetingUpsertPayload) => {
  const resp = await api.post<{ series_id: string; meetings: MeetingResponse[] }>("/api/recurrences/", payload)
  return resp.data
}

const updateRecurrence = async (seriesId: string, payload: MeetingUpsertPayload) => {
  const resp = await api.patch<{ series_id: string; meetings: MeetingResponse[] }>(`/api/recurrences/${seriesId}/`, payload)
  return resp.data
}

const recordWaitRoomAdmission = async (
  meetingId: string,
  payload: { display_name?: string; daily_participant_id?: string } = {},
) => {
  const resp = await api.post<{ status: string }>(`/api/meetings/${meetingId}/wait-room-admission/`, payload)
  return resp.data
}

const confirmMeetingJoin = async (
  meetingId: string,
  payload: { display_name?: string; daily_participant_id: string },
) => {
  const resp = await api.post<{ status: string }>(`/api/meetings/${meetingId}/confirm-join/`, payload)
  return resp.data
}

const unlockWaitRoom = async (meetingId: string, panelToken?: string) => {
  const resp = await api.post<{
    status: string
    waiting_room_enabled: boolean
    waiting_room_unlocked: boolean
    waiting_room_unlocked_at?: string | null
  }>(`/api/meetings/${meetingId}/unlock-wait-room/`, panelToken ? { panel_token: panelToken } : {})
  return resp.data
}

const splitRecurrence = async (seriesId: string, payload: MeetingUpsertPayload & { effective_meeting_id: string }) => {
  const resp = await api.post<{ series_id: string; meetings: MeetingResponse[] }>(`/api/recurrences/${seriesId}/split/`, payload)
  return resp.data
}

const cancelRecurrence = async (
  seriesId: string,
  payload: { scope: "series" | "following"; effective_meeting_id?: string },
) => {
  await api.post(`/api/recurrences/${seriesId}/cancel/`, payload)
}

const deleteRecurrence = async (seriesId: string) => {
  await api.delete(`/api/recurrences/${seriesId}/`)
}

const resetMeetingToSeries = async (meetingId: string) => {
  const resp = await api.post<MeetingResponse>(`/api/meetings/${meetingId}/reset-to-series/`)
  return resp.data
}

const restoreMeeting = async (meetingId: string) => {
  const resp = await api.post<MeetingResponse>(`/api/meetings/${meetingId}/restore/`)
  return resp.data
}

const getAdminDashboard = async (params?: { cohort_id?: string }) => {
  const resp = await api.get<AdminDashboardResponse>("/api/meetings/admin-dashboard/", { params })
  return resp.data
}

export type MobileMeetingAccess = {
  access_id: string
  state: "waiting" | "admitted" | "denied" | "left" | "removed"
  lifecycle_token: string
  ended?: boolean
  cancelled?: boolean
  url?: string
  token?: string
  can_start_transcription?: boolean
  poll_panel?: MeetingPollPanel
}
export type MobileMeetingWaiter = {
  id: string
  display_name: string
  state: "waiting"
  created_at: string
}
const prepareMobileAccess = async (meetingId: string, payload: { panel_token: string; breakout_token?: string | null; guest_token?: string | null }) => {
  const response = await api.post<MobileMeetingAccess>(`/api/meetings/${meetingId}/mobile-access/`, payload)
  return response.data
}
const getMobileAccess = async (meetingId: string, lifecycleToken: string) => {
  const response = await api.get<MobileMeetingAccess>(`/api/meetings/${meetingId}/mobile-access/`, { headers: { "X-Meeting-Panel-Token": lifecycleToken } })
  return response.data
}
const cancelMobileAccess = async (meetingId: string, lifecycleToken: string) => {
  const response = await api.delete<{ id: string; state: "left" | "denied" }>(`/api/meetings/${meetingId}/mobile-access/`, { headers: { "X-Meeting-Panel-Token": lifecycleToken } })
  return response.data
}
const getMobileWaiters = async (meetingId: string) => {
  const response = await api.get<{ participants: MobileMeetingWaiter[] }>(`/api/meetings/${meetingId}/mobile-waiters/`)
  return response.data
}
const decideMobileAccess = async (meetingId: string, accessId: string, action: "admit" | "deny") => {
  const response = await api.post<{ id: string; state: "admitted" | "denied" }>(`/api/meetings/${meetingId}/mobile-waiters/${accessId}/`, { action })
  return response.data
}

export type MobileMeetingRemoval = { revoked: true; scope: "meeting_identity"; ejected: boolean; removal_receipt?: string }

/** Revoke this meeting identity's admission before any best-effort SDK ejection. */
const removeMobileMeetingParticipant = async (meetingId: string, roomName: string, participantId: string) => {
  const response = await api.post<MobileMeetingRemoval>(
    `/api/meetings/${meetingId}/mobile-remove-participant/`, { room_name: roomName, participant_id: participantId },
  )
  return response.data
}

/** Retry only delivery of an already-authorized removal. The opaque receipt stays in the body. */
const retryMobileMeetingRemoval = async (meetingId: string, removalReceipt: string) => {
  const response = await api.post<MobileMeetingRemoval>(`/api/meetings/${meetingId}/mobile-remove-participant/`, { removal_receipt: removalReceipt })
  return response.data
}

export const meetings = {
  fetchNativeBreakoutContext,
  renewNativeRoomTarget,
  renewNativeCallTools,
  removeMobileMeetingParticipant,
  retryMobileMeetingRemoval,
  prepareMobileAccess,
  cancelMobileAccess,
  getMobileAccess,
  getMobileWaiters,
  decideMobileAccess,
  getMeetings,
  getMeeting,
  getMeetingAttendance,
  saveMeetingAttendance,
  downloadResource,
  downloadTranscript,
  downloadRecording,
  getMeetingQuestions,
  getMeetingQuestionsPanel,
  getMeetingMessagesPanel,
  submitMeetingQuestion,
  submitMeetingQuestionsPanelQuestion,
  updateMeetingMessagesPresence,
  createMeetingDirectMessageThread,
  getMeetingDirectMessageThreadMessages,
  sendMeetingDirectMessage,
  markMeetingDirectMessageThreadRead,
  updateMeetingQuestion,
  updateMeetingQuestionsPanelQuestion,
  createMeeting,
  updateMeeting,
  deleteMeeting,
  joinMeeting,
  joinGuestMeeting,
  recordWaitRoomAdmission,
  confirmMeetingJoin,
  unlockWaitRoom,
  submitGuestMeetingQuestion,
  endMeeting,
  createBreakoutSession,
  fetchBreakoutCurrent,
  fetchGuestBreakoutCurrent,
  updateBreakoutAssignments,
  broadcastBreakoutMessage,
  endBreakoutSession,
  createRecurrence,
  updateRecurrence,
  splitRecurrence,
  cancelRecurrence,
  deleteRecurrence,
  resetMeetingToSeries,
  restoreMeeting,
  getAdminDashboard,
  getMeetingPollPanel,
  admitMeetingPollParticipant,
  createMeetingPoll,
  saveMeetingPollVote,
  controlMeetingPoll,
  getMeetingPollResponses,
  getMeetingPollHistory,
  getMeetingInspirePanel,
  createMeetingInspireActivity,
  endMeetingInspireActivity,
  saveMeetingInspireResponse,
  getMeetingInspireResponses,
  shareMeetingInspireResponse,
  getMeetingInspireHistory,
}
